import { Dispatch, SetStateAction } from "react"

// ** UI Components
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@pma/components/common/alert-dialog"
import { Button } from "@pma/components/common/button"
import { Label } from "@pma/components/common/label"

// ** Types
import { DictType } from "@pma/types"

// ** Custom Hooks
import useTranslator from "@pma/hooks/use-translator"

// ** Custom Components
import { DatePicker } from "@pma/pages/Project/Task/TaskBody/WorkDiaryView/WorkLogDiary/shared"

interface CloneConfirmDialogProps {
  cloneConfirmDiary: DictType | null
  setCloneConfirmDiary: Dispatch<SetStateAction<DictType | null>>
  cloneDate: string
  setCloneDate: Dispatch<SetStateAction<string>>
  isPending: boolean
  onClone: (diaryId: number, date: string) => void
}

const CloneConfirmDialog = ({
  cloneConfirmDiary,
  setCloneConfirmDiary,
  cloneDate,
  setCloneDate,
  isPending,
  onClone,
}: CloneConfirmDialogProps) => {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"

  const handleClose = () => {
    setCloneConfirmDiary(null)
    setCloneDate("")
  }

  return (
    <AlertDialog
      open={!!cloneConfirmDiary}
      onOpenChange={(open) => {
        if (!open) handleClose()
      }}
    >
      <AlertDialogContent className={isHebrew ? "rtl" : "ltr"}>
        <AlertDialogHeader>
          <AlertDialogTitle className={isHebrew ? "text-right" : "text-left"}>
            {translate("Proceed to clone")} {cloneConfirmDiary?.number}?
          </AlertDialogTitle>
          <AlertDialogDescription className={isHebrew ? "text-right" : "text-left"}>
            {translate("This action will create a copy of the work diary with a new date")}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className='px-1 py-2'>
          <Label className='mb-2 block text-sm font-medium'>{translate("New Diary Date")}</Label>
          <DatePicker
            value={cloneDate}
            onChange={(value) => setCloneDate(value)}
            placeholder={translate("Select a date")}
          />
        </div>
        <AlertDialogFooter className={isHebrew ? "!space-x-reverse" : ""}>
          <AlertDialogCancel onClick={handleClose}>{translate("Cancel")}</AlertDialogCancel>
          <Button
            disabled={!cloneDate || isPending}
            loading={isPending}
            onClick={() => {
              if (cloneConfirmDiary?.id && cloneDate) {
                onClone(cloneConfirmDiary.id, cloneDate)
              }
            }}
          >
            {translate("Continue")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default CloneConfirmDialog
